import type { ValueOf } from '../../../types/ValueOf';
import type { vendorApiNames } from '../names';
import type {
  BaseUpdateVendorApiSettings,
  BaseUrl,
  BaseVendorApi,
  RejectUnauthorized,
} from './base';
import type {
  VendorApiHttpAdvancedSettings,
  VendorApiHttpAgentSettings,
} from './http';
import type { VendorApiSessionSettings } from './session';

/**
 * Which OAuth flow the New Central client uses to obtain access tokens:
 *
 * - `central` — today's flow: a `client_credentials` grant against the fixed HPE
 *   SSO endpoint ({@link arubaNewCentralDefaultTokenUrl}). ~2h token TTL.
 * - `glp` — GreenLake Platform: a `client_credentials` grant against the
 *   workspace-scoped GreenLake token endpoint. Same data plane; ~15-min TTL.
 * - `msp` — a GreenLake parent token exchanged (RFC 8693) for a per-tenant
 *   token, with two-level discovery (list tenants → devices per tenant).
 */
export const arubaAuthTypes = {
  central: 'central',
  glp: 'glp',
  msp: 'msp',
} as const;

export type ArubaAuthType = ValueOf<typeof arubaAuthTypes>;

export interface ArubaNewCentralConnectionSettings {
  /**
   * The auth flow to use. `tenantId` is required for `glp`/`msp` and forbidden
   * for `central`.
   */
  loginType: ArubaAuthType;
  /**
   * Tenant workspace id.
   */
  tenantId?: string;
}

export interface ArubaNewCentralAuthentication {
  /**
   * OAuth application client id created in the New Central / GreenLake console.
   */
  clientId: string;
  /**
   * OAuth application client secret.
   */
  clientSecret: string;
}

/**
 * Aruba New Central API settings available from API
 */
export type PublicArubaNewCentralApiSettings = BaseVendorApi<
  (typeof vendorApiNames)['arubaNewCentral']
> &
  BaseUrl &
  RejectUnauthorized &
  VendorApiHttpAgentSettings &
  VendorApiHttpAdvancedSettings &
  VendorApiSessionSettings &
  ArubaNewCentralConnectionSettings;

/**
 * Aruba New Central API settings available in backend
 */
export type ArubaNewCentralApiSettings = PublicArubaNewCentralApiSettings &
  ArubaNewCentralAuthentication;

/**
 * Aruba New Central API settings expected on API when updating
 */
export type UpdateArubaNewCentralApiSettings = BaseUpdateVendorApiSettings<
  PublicArubaNewCentralApiSettings,
  ArubaNewCentralAuthentication
>;
